/**
 * product-variant-media.js
 *
 * Two features on the product page gallery:
 *
 * 1. ALT-TAG IMAGE FILTERING (client-side)
 *    Images whose alt text follows the #OptionFirstWord_Value convention are
 *    filtered purely in the browser when the variant changes.
 *    Each tagged <li> carries data-alt-filter="OptionFirstWord:value".
 *    <media-gallery> carries data-option-names='["Color","Size",...]'.
 *    On variantChange: JS toggles `hidden`, then navigates to the first
 *    visible slide (or the variant's featured_media if one is set).
 *
 * 2. VARIANT VIDEO METAFIELD (custom.video_url)
 *    <media-gallery> carries data-variant-videos='{"variantId":"url",...}'.
 *    Inspired by DORO's native-video reset approach: whenever the user navigates
 *    AWAY from the video slide, we reset the deferred-media back to its poster
 *    state (remove `loaded` attr + live <video> element). This guarantees that
 *    every visit to the video slide triggers a clean loadContent() call:
 *    poster → user clicks → video plays. No fighting with pauseAllMedia().
 */
(function () {

  // --------------------------------------------------------------------------
  // Helpers
  // --------------------------------------------------------------------------

  function buildOptionMap(variant, optionNames) {
    var map = {};
    var values = [variant.option1, variant.option2, variant.option3];
    optionNames.forEach(function (name, i) {
      var firstWord = name.split(' ')[0];
      map[firstWord] = (values[i] || '').toLowerCase();
    });
    return map;
  }

  // Preload all images inside an element so they're cached before the slide
  // becomes visible. Uses <link rel=preload imagesrcset> so the browser fetches
  // the SAME srcset candidate it will render (not just the fallback src).
  // A data-attr flag prevents duplicate <link> tags on repeated calls.
  function preloadImagesIn(el) {
    if (!el) return;
    el.querySelectorAll('img').forEach(function (img) {
      // Always mark eager so it loads the moment its parent becomes visible
      img.loading = 'eager';
      if (img.dataset.preloadQueued) return;
      img.dataset.preloadQueued = '1';
      // Inject <link rel=preload as=image> — fires fetch from outside display:none
      var link = document.createElement('link');
      link.rel = 'preload';
      link.as = 'image';
      if (img.srcset) {
        link.setAttribute('imagesrcset', img.srcset);
        link.setAttribute('imagesizes', img.sizes || '100vw');
      }
      if (img.src) link.href = img.src;
      document.head.appendChild(link);
    });
  }

  function applyAltFilter(mediaGallery, optionMap) {
    // Find the first slide that matches the current variant and return it for navigation.
    // All slides stay visible at all times — no hiding, no loading issues.
    function isMatch(altFilter) {
      var conditions = altFilter.split('|');
      for (var c = 0; c < conditions.length; c++) {
        var parts = conditions[c].split(':');
        var selectedValue = optionMap[parts[0]];
        if (selectedValue !== undefined && selectedValue !== (parts[1] || '')) return false;
      }
      return true;
    }

    var allSlides = mediaGallery.querySelectorAll('li[data-media-id]');
    for (var i = 0; i < allSlides.length; i++) {
      var filter = allSlides[i].dataset.altFilter;
      if (!filter || isMatch(filter)) return allSlides[i];
    }
    return allSlides[0] || null;
  }

  function navigateTo(mediaGallery, mediaId) {
    if (!mediaId) return;
    if (typeof mediaGallery.setActiveMedia === 'function') {
      mediaGallery.setActiveMedia(mediaId, true);
    } else {
      var viewer = mediaGallery.querySelector('[id^="GalleryViewer-"] ul');
      var target = mediaGallery.querySelector('[data-media-id="' + mediaId + '"]');
      if (viewer && target) viewer.scrollTo({ left: target.offsetLeft, behavior: 'smooth' });
    }
  }

  /**
   * Reset a deferred-media element back to its poster state.
   * Mirrors what DORO's custom.js does for native-video on variant change:
   * remove the live element, remove the `loaded` guard, restore the poster.
   * The <template> stays untouched so the next loadContent() call works cleanly.
   */
  function resetDeferredMedia(deferred) {
    if (!deferred) return;

    // Remove live video (or any already-cloned media child)
    var liveVideo = deferred.querySelector('video');
    if (liveVideo) liveVideo.remove();

    // Lift the one-shot guard so loadContent() fires properly next time
    deferred.removeAttribute('loaded');

    // Restore poster visibility (loadContent hides it via display:none in some themes)
    var poster = deferred.querySelector('[id^="Deferred-Poster-"]');
    if (poster) poster.style.display = '';
  }

  // --------------------------------------------------------------------------
  // Patch setActiveMedia on a gallery to reset the metafield video slide
  // whenever the user navigates AWAY from it.
  // --------------------------------------------------------------------------

  function patchGalleryForVideoReset(mediaGallery) {
    if (mediaGallery._videoResetPatched) return;
    mediaGallery._videoResetPatched = true;

    var original = mediaGallery.setActiveMedia.bind(mediaGallery);
    mediaGallery.setActiveMedia = function (mediaId, prepend) {
      // Reset any video slide we're navigating away from so next visit is fresh
      mediaGallery.querySelectorAll('[data-variant-video-slide]').forEach(function (slide) {
        if (slide.dataset.mediaId !== mediaId) {
          resetDeferredMedia(slide.querySelector('.deferred-media'));
        }
      });
      return original(mediaId, prepend);
    };
  }

  // --------------------------------------------------------------------------
  // Variant change handler
  // --------------------------------------------------------------------------

  function handleVariantChange(event) {
    var variant = event.data && event.data.variant;
    if (!variant) return;

    var sectionId = event.data.sectionId;
    var mediaGallery = sectionId
      ? document.querySelector('#MediaGallery-' + sectionId)
      : document.querySelector('media-gallery');
    if (!mediaGallery) return;

    // Re-apply the patch in case AJAX replaced the gallery instance
    if (typeof mediaGallery.setActiveMedia === 'function') {
      patchGalleryForVideoReset(mediaGallery);
    }

    // --- 1. Navigate to featured media / alt-tag filtering ---
    if (variant.featured_media) {
      // Navigate directly to this variant's featured media.
      // product-info.js also calls setActiveMedia, but its scroll is conditional
      // (only fires on mobile or with thumbnails). navigateTo here ensures the
      // gallery scrolls on all layouts.
      navigateTo(mediaGallery, sectionId + '-' + variant.featured_media.id);
    }
    // When variant has no featured_media, leave the gallery on its current slide.

    // Alt-tag filtering (runs only when data-option-names is present)
    var optionNamesAttr = mediaGallery.getAttribute('data-option-names');
    if (optionNamesAttr) {
      var optionNames;
      try { optionNames = JSON.parse(optionNamesAttr); } catch (e) { optionNames = []; }

      if (optionNames.length) {
        var optionMap = buildOptionMap(variant, optionNames);
        var firstVisible = applyAltFilter(mediaGallery, optionMap);
        // Alt-filter navigation only for no-media variants (featured_media case handled above)
        if (!variant.featured_media && firstVisible) {
          navigateTo(mediaGallery, firstVisible.dataset.mediaId);
        }
      }
    }

    // --- 2. Variant video metafield (per-color slides) ---
    // All video slides stay visible at all times — video resets are handled by
    // patchGalleryForVideoReset when the user navigates to a different slide.
  }

  // --------------------------------------------------------------------------
  // Init
  // --------------------------------------------------------------------------

  function init() {
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.variantChange, handleVariantChange);
    }

    document.querySelectorAll('media-gallery').forEach(function (gallery) {
      if (typeof gallery.setActiveMedia === 'function') {
        patchGalleryForVideoReset(gallery);
      }
    });
  }

  // global.js deferred scripts run before DOMContentLoaded, so MediaGallery
  // is already defined; but setActiveMedia is only available after the custom
  // element constructor runs (on element upgrade). Use a short rAF to be safe.
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      requestAnimationFrame(init);
    });
  } else {
    requestAnimationFrame(init);
  }

})();
